import { useState, useMemo } from 'react';
import { EshkolLogo } from '@/components/EshkolLogo';
import { ProductLogo } from '@/components/ProductLogo';
import { useGeoLayer, type EnrichedFeature } from '@/hooks/useGeoLayer';
import { useTranslation } from '@/i18n/useTranslation';
import { useMondayBoardItems, type MondayItem } from '@/hooks/useMondayBoardItems';
import { AgriTourSiteHeader } from '@/screens/AgriTour/AgriTourChrome';
import { AgriTourMapToolbar } from '@/screens/AgriTour/AgriTourMapToolbar';
import agritourStyles from '@/screens/AgriTour/AgriTourMapScreen.module.css';
import { LAYER_OPTIONS } from './layer-options';
import { SiteListPanel } from './SiteListPanel';
import { SiteMap } from './SiteMap';
import { SiteDrawer } from './SiteDrawer';
import { SyncControlPanel } from './SyncControlPanel';
import { GeoJsonExportBar } from './GeoJsonExportBar';
import styles from './TourismMapScreen.module.css';

function joinMondayItem(feature: EnrichedFeature, mondayItems: MondayItem[]): MondayItem | null {
  const name = feature.name.toLowerCase().trim();
  return mondayItems.find((item) => item.name.toLowerCase().trim() === name) ?? null;
}

export type TourismMapScreenProps = {
  /** When set, locks the view to one layer and hides the layer selector. */
  fixedLayerId?: string;
  title?: string;
  loadingLabel?: string;
  variant?: 'default' | 'agritour';
  syncPanelMode?: 'full' | 'geojson-only' | 'hidden';
};

export default function TourismMapScreen({
  fixedLayerId,
  title = 'מפת תיירות - גליל מזרחי',
  loadingLabel = 'טוען נתוני GIS...',
  variant = 'default',
  syncPanelMode = 'full',
}: TourismMapScreenProps = {}) {
  const t = useTranslation();
  const isAgritour = variant === 'agritour';
  const shellStyles = isAgritour ? agritourStyles : styles;
  const [layerId, setLayerId] = useState<string>(fixedLayerId ?? LAYER_OPTIONS[0].id);
  const [selected, setSelected] = useState<EnrichedFeature | null>(null);

  const { features, loading: geoLoading, error: geoError } = useGeoLayer(layerId);
  const isMondayBackedLayer =
    layerId === 'entrepreneurs' || layerId === 'agritour-entrepreneurs';
  const { items: mondayItems, loading: mondayLoading } = useMondayBoardItems({
    enabled: !isMondayBackedLayer,
  });

  const matchedMondayItem = useMemo(
    () => (selected ? joinMondayItem(selected, mondayItems) : null),
    [selected, mondayItems],
  );

  const handleSelect = (feature: EnrichedFeature) => {
    setSelected((prev) => (prev?.id === feature.id ? null : feature));
  };

  const showFullPageLoader = geoLoading && features.length === 0;

  if (showFullPageLoader) {
    return (
      <div className={shellStyles.loading}>
        <div className={shellStyles.loadingInner}>
          {!isAgritour && (
            <div className={styles.loadingBrands}>
              <ProductLogo variant="full" compact decorative />
              <EshkolLogo alt={t.landing.eshkolLogoAlt} className={styles.eshkolLogo} />
            </div>
          )}
          <span className={shellStyles.spinner} />
          <p className={shellStyles.loadingLabel}>{loadingLabel}</p>
        </div>
      </div>
    );
  }

  if (geoError) {
    return (
      <div className={shellStyles.error}>
        <div className={shellStyles.errorBox}>
          <p className={shellStyles.errorLabel}>שגיאה בטעינת נתונים: {geoError}</p>
        </div>
      </div>
    );
  }

  const withGeoCount = features.filter((f) => f.geometry).length;
  const polygonCount = features.filter((f) => f.geometry?.shape).length;
  const isEntrepreneursLayer =
    layerId === 'entrepreneurs' || layerId === 'agritour-entrepreneurs';
  const entityLabel =
    layerId === 'agritour-entrepreneurs'
      ? 'בקשות'
      : isEntrepreneursLayer
        ? 'יזמים'
        : 'אתרים';

  const syncPanel =
    syncPanelMode === 'hidden' ? null : syncPanelMode === 'geojson-only' ? (
      <GeoJsonExportBar layerId={layerId} features={features} mapLoading={geoLoading} />
    ) : (
      <SyncControlPanel layerId={layerId} features={features} mapLoading={geoLoading} />
    );

  return (
    <div className={shellStyles.screen}>
      {isAgritour ? (
        <>
          <AgriTourSiteHeader variant="map" />
          <AgriTourMapToolbar
            title={title}
            layerId={layerId}
            features={features}
            mapLoading={geoLoading}
            totalCount={features.length}
            withGeoCount={withGeoCount}
            polygonCount={polygonCount}
          />
          <div className={agritourStyles.mapWorkspace}>
            <SiteListPanel
              features={features}
              selectedId={selected?.id ?? null}
              onSelect={handleSelect}
              layerId={layerId}
              variant="agritour"
              className={agritourStyles.listPanel}
            />
            <div className={agritourStyles.mapCard}>
              <SiteMap features={features} selected={selected} onSelect={handleSelect} />
              {selected && (
                <SiteDrawer
                  feature={selected}
                  mondayItem={matchedMondayItem}
                  onClose={() => setSelected(null)}
                  variant="agritour"
                />
              )}
            </div>
          </div>
        </>
      ) : (
        <header className={styles.header}>
          <div className={styles.headerRow}>
            <div className={styles.brandBlock}>
              <ProductLogo variant="full" compact decorative />
              <span className={styles.headerDivider} aria-hidden />
              <EshkolLogo alt={t.landing.eshkolLogoAlt} className={styles.eshkolLogo} />
            </div>

            <div className={styles.headerCenter}>
              <h1 className={styles.headerTitle}>{title}</h1>
              {!fixedLayerId ? (
                <select
                  className={styles.layerSelect}
                  value={layerId}
                  onChange={(e) => { setLayerId(e.target.value); setSelected(null); }}
                >
                  {LAYER_OPTIONS.map((l) => (
                    <option key={l.id} value={l.id}>{l.displayName}</option>
                  ))}
                </select>
              ) : null}
            </div>

            <span className={styles.headerCounts}>
              {features.length} {entityLabel} · {withGeoCount}{' '}
              {isEntrepreneursLayer ? 'עם גאומטריה' : 'עם מיקום'}
              {isEntrepreneursLayer && polygonCount > 0 ? ` · ${polygonCount} מצוירים` : ''}
              {!isEntrepreneursLayer && (
                mondayLoading
                  ? ' · טוען...'
                  : mondayItems.length > 0
                    ? ` · ${mondayItems.length} Monday`
                    : ''
              )}
            </span>
          </div>

          {syncPanel}
        </header>
      )}

      {!isAgritour ? (
      <div className={shellStyles.body}>
        <SiteListPanel
          features={features}
          selectedId={selected?.id ?? null}
          onSelect={handleSelect}
          layerId={layerId}
        />

        <div className={shellStyles.mapCard}>
          <SiteMap features={features} selected={selected} onSelect={handleSelect} />
          {selected && (
            <SiteDrawer
              feature={selected}
              mondayItem={matchedMondayItem}
              onClose={() => setSelected(null)}
            />
          )}
        </div>
      </div>
      ) : null}
    </div>
  );
}
